/* PreProductionGateModal — iter97.24
 *
 * Full-screen 2-step gate that intercepts the production-tech "Acknowledge
 * & begin" action. Until both steps complete the case is NOT in production,
 * inventory is NOT touched, and the workflow timer is NOT started.
 *
 *   Step 1 — Manufacturing Spec Review
 *     • Admin routing note (if any)
 *     • Planner note (read-only)
 *     • Aligner / Retainer / 3D Print spec cards
 *     • Required confirmation checkbox
 *
 *   Step 2 — Material Pickup Log
 *     • One pre-filled row per BOM line (Aligner-Upper / Aligner-Lower /
 *       Retainer / Resin-Upper / Resin-Lower / IPA / Packaging)
 *     • Cascading dropdowns (SKU + Variant + Batch) + qty input
 *     • Substitution detection w/ amber warning + mandatory reason
 *     • "+ Add material not in BOM" row (purple ADDITIONAL badge)
 *     • Stock-availability summary bar (green / amber / red)
 *     • Inventory deduction preview line
 *     • Confirm disabled until all required fields are valid
 *
 *   On Confirm:
 *     1. POST /api/production/cases/{id}/acknowledge   (file_integrity_verified=true)
 *     2. POST /api/production/cases/{id}/materials-log (with the full row payload)
 *     3. Toast + dismiss + refresh parent
 *     4. Parent reloads workflow → Step 1 of the 10-stage timeline activates
 *
 * The modal is NOT dismissible on backdrop click. The user must Cancel
 * (Step 1 only) or Confirm to leave it.
 */
import React, { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
    Dialog, DialogContent,
} from "@/components/ui/dialog";
import {
    Bell, BookmarkSimple, Stack, Drop, Tooth, Crown, Package, Flask, GearSix,
    Warning, CheckCircle, ArrowsLeftRight, ArrowRight, ArrowLeft, Lock,
    PlayCircle, Cube,
} from "@phosphor-icons/react";


/* ───── Reuse the category/tone/reason vocabularies from MaterialsUsedPanel
 *      so the gate matches the in-case panel exactly. */

const CATEGORIES = [
    { id: "tpu_sheet",       label: "Aligner sheet",     Icon: Stack,    tone: "violet" },
    { id: "retainer_sheet",  label: "Retainer sheet",    Icon: Tooth,    tone: "sky" },
    { id: "resin",           label: "Resin",             Icon: Drop,     tone: "amber" },
    { id: "ipa",             label: "IPA / Cleaning",    Icon: Flask,    tone: "emerald" },
    { id: "packaging",       label: "Packaging",         Icon: Package,  tone: "rose" },
    { id: "consumables",     label: "Consumable",        Icon: GearSix,  tone: "slate" },
    { id: "other",           label: "Other",             Icon: GearSix,  tone: "slate" },
];
const CAT = Object.fromEntries(CATEGORIES.map((c) => [c.id, c]));

const TONE_BG = {
    violet:  "bg-violet-100 text-violet-700",
    sky:     "bg-sky-100 text-sky-700",
    amber:   "bg-amber-100 text-amber-700",
    emerald: "bg-emerald-100 text-emerald-700",
    rose:    "bg-rose-100 text-rose-700",
    slate:   "bg-slate-100 text-slate-700",
};

const SUBSTITUTION_REASONS = [
    { value: "out_of_stock",          label: "Specified material out of stock" },
    { value: "defective_batch",       label: "Defective batch — replaced" },
    { value: "admin_approved",        label: "Admin approved substitution" },
    { value: "tech_error_corrected",  label: "Technician error — corrected" },
    { value: "other",                 label: "Other (see note)" },
];

const PURPOSE_LABEL = {
    aligner_sheet_upper: "Aligner Sheet — Upper Arch",
    aligner_sheet_lower: "Aligner Sheet — Lower Arch",
    retainer_upper:      "Retainer Sheet — Upper",
    retainer_lower:      "Retainer Sheet — Lower",
    ipa:                 "IPA / Cleaning Solution",
    resin:               "3D Print Resin",
    packaging:           "Packaging Kit",
};


function categoryFromPurpose(p) {
    if (!p) return "other";
    if (p.startsWith("aligner_sheet")) return "tpu_sheet";
    if (p.startsWith("retainer")) return "retainer_sheet";
    if (p === "resin") return "resin";
    if (p === "ipa") return "ipa";
    if (p === "packaging") return "packaging";
    return "other";
}

function round1(n) {
    return Math.round(Number(n || 0) * 10) / 10;
}


export default function PreProductionGateModal({ open, caseObj, onClose, onConfirmed }) {
    const caseId = caseObj?.id;

    /* ── State ──────────────────────────────────────────────────────── */
    const [step, setStep] = useState(1);
    const [loading, setLoading] = useState(true);
    const [confirmed1, setConfirmed1] = useState(false);
    const [bom, setBom] = useState(null);
    const [allMaterials, setAllMaterials] = useState([]);
    const [batchesByMat, setBatchesByMat] = useState({});
    const [plannerNote, setPlannerNote] = useState(null);
    const [rows, setRows] = useState([]);
    const [submitting, setSubmitting] = useState(false);
    /* iter97.26 — Phase-aware Step 2.
       phaseScope is computed once when BOM loads. If the planner authored
       phases on the manufacturing_spec, we scope Step 2 to the NEXT
       un-shipped phase (initial gate = Phase 1). Materials for later
       phases will be pulled in subsequent gates after each phase ships. */
    const [phaseScope, setPhaseScope] = useState(null);
    /* Per-row computed phase quantities (so user can still edit) */

    /* Reset on every open */
    useEffect(() => {
        if (!open) return;
        setStep(1);
        setConfirmed1(false);
        setSubmitting(false);
        if (!caseId) return;
        setLoading(true);
        Promise.all([
            api.get(`/cases/${caseId}/expected-bom`),
            api.get(`/admin/inventory/materials`).catch(() => ({ data: [] })),
            api.get(`/cases/${caseId}/planner-notes-for-production`).catch(() => ({ data: null })),
        ])
            .then(async ([bRes, mRes, pRes]) => {
                setBom(bRes.data);
                setAllMaterials(mRes.data || []);
                setPlannerNote(pRes.data);
                const lines = bRes.data?.lines || [];
                // Pre-load batches for each BOM material
                const map = {};
                for (const l of lines) {
                    if (!l.material_id || map[l.material_id]) continue;
                    try {
                        const r = await api.get(`/admin/inventory/batches?material_id=${l.material_id}&status=active`);
                        map[l.material_id] = r.data || [];
                    } catch {
                        map[l.material_id] = [];
                    }
                }
                setBatchesByMat(map);

                /* iter97.26 — Compute phase scope BEFORE seeding rows.
                   - If planner authored phases, target the FIRST phase.
                   - Else single-phase (whole-case) → scope is null and
                     rows are seeded with full BOM quantities. */
                const spec = caseObj?.manufacturing_spec || {};
                const phases = spec.phases || [];
                const totalStages = spec.aligner_stages_count || 0;
                const sheetsPerStage = spec.sheets_per_stage || 1;
                let scope = null;
                if (phases.length > 0 && totalStages > 0) {
                    /* Find the next un-shipped phase. Today we don't yet
                       persist phase-shipment progress, so the initial gate
                       always targets index 0. */
                    const idx = 0;
                    const ph = phases[idx];
                    const phaseStages = ph.stage_to - ph.stage_from + 1;
                    scope = {
                        index: idx,
                        total: phases.length,
                        stage_from: ph.stage_from,
                        stage_to: ph.stage_to,
                        stages_in_phase: phaseStages,
                        total_stages: totalStages,
                        is_last: idx === phases.length - 1,
                        phase_sheets_per_arch: phaseStages,
                        // ratio used to scale resin/IPA from full-case to phase
                        ratio: totalStages > 0 ? phaseStages / totalStages : 1,
                    };
                }
                setPhaseScope(scope);

                /* iter97.27 — Filter BOM lines to only RAW MATERIALS
                   consumed during the production run (aligner sheets,
                   retainer sheets, resin). IPA + packaging are deducted
                   at later workflow stages, NOT at the pre-production
                   gate. */
                const PRODUCTION_RUN_PURPOSES = new Set([
                    "aligner_sheet_upper", "aligner_sheet_lower",
                    "aligner_sheet",  // legacy fallback
                    "retainer_upper", "retainer_lower",
                    "retainer",  // legacy fallback
                    "resin",
                ]);
                const filteredLines = lines.filter((l) => PRODUCTION_RUN_PURPOSES.has(l.bom_purpose));

                /* Seed rows — applying phase scope when present. Skip
                   retainer lines on non-final phases (retainers fab
                   at the very end). Scale aligner sheets / resin. */
                const seeded = [];
                for (const l of filteredLines) {
                    const purpose = l.bom_purpose || "";
                    /* Retainer: only on final phase (or when no phases). */
                    if (scope && !scope.is_last && purpose.startsWith("retainer")) {
                        continue;
                    }
                    /* Compute phase-scoped expected qty. */
                    let phaseQty = Number(l.expected_qty || 0);
                    let qtySource = "bom";
                    if (scope) {
                        if (purpose === "aligner_sheet_upper" || purpose === "aligner_sheet_lower") {
                            phaseQty = scope.phase_sheets_per_arch;
                            qtySource = "phase_aligner";
                        } else if (purpose === "resin") {
                            phaseQty = round1(Number(l.expected_qty || 0) * scope.ratio);
                            qtySource = "phase_ratio";
                        }
                    }
                    seeded.push({
                        kind: "bom",
                        bom_purpose: l.bom_purpose,
                        arch: l.arch,
                        row_label: l.row_label || PURPOSE_LABEL[l.bom_purpose] || l.bom_purpose,
                        material_id: l.material_id,
                        material_default_sku: l.material_sku,
                        material_default_name: l.material_name,
                        category: categoryFromPurpose(l.bom_purpose),
                        chosen_material_id: l.material_id,
                        variant_id: "",
                        variant_label: "",
                        batch_id: "",
                        batch_lot: "",
                        bom_expected_qty: Number(l.expected_qty || 0),
                        expected_qty: phaseQty,
                        actual_qty: phaseQty,
                        qty_source: qtySource,
                        substitution_reason: "",
                        note: "",
                        current_stock: l.current_stock,
                        reorder_threshold: l.reorder_threshold,
                        unit: l.unit,
                    });
                }
                setRows(seeded);
            })
            .catch((e) => {
                toast.error(e.response?.data?.detail || "Failed to load case context");
                onClose?.();
            })
            .finally(() => setLoading(false));
    }, [open, caseId]); // eslint-disable-line react-hooks/exhaustive-deps

    /* ── Row mutators (small surface, scoped to step 2) ──────────────── */
    const fetchBatchesFor = async (mid) => {
        if (!mid || batchesByMat[mid] != null) return;
        try {
            const r = await api.get(`/admin/inventory/batches?material_id=${mid}&status=active`);
            setBatchesByMat((p) => ({ ...p, [mid]: r.data || [] }));
        } catch {
            setBatchesByMat((p) => ({ ...p, [mid]: [] }));
        }
    };
    const update = (idx, patch) => setRows((p) => p.map((r, i) => (i === idx ? { ...r, ...patch } : r)));
    const setChosenMaterial = (idx, mid) => {
        const mat = allMaterials.find((m) => m.id === mid);
        update(idx, {
            chosen_material_id: mid,
            variant_id: "",
            variant_label: "",
            batch_id: "",
            batch_lot: "",
            current_stock: mat?.current_stock ?? null,
            unit: mat?.unit ?? null,
        });
        fetchBatchesFor(mid);
    };
    const setVariant = (idx, vid) => {
        const row = rows[idx];
        const mat = allMaterials.find((m) => m.id === row.chosen_material_id);
        const v = (mat?.variants || []).find((x) => x.id === vid);
        update(idx, { variant_id: vid, variant_label: v?.label || "" });
    };
    // eslint-disable-next-line no-unused-vars
    const _unused_setVariant = setVariant; // kept for future variant-aware spec authoring
    const setBatch = (idx, bid) => {
        const row = rows[idx];
        const batch = (batchesByMat[row.chosen_material_id] || []).find((b) => b.id === bid);
        update(idx, { batch_id: bid, batch_lot: batch?.lot_number || "" });
    };
    const setBatchFreeText = (idx, lot) => update(idx, { batch_id: "", batch_lot: lot });
    const setCategory = (idx, cat) => {
        if (rows[idx].kind !== "additional") return;
        update(idx, {
            category: cat,
            chosen_material_id: "",
            variant_id: "", variant_label: "",
            batch_id: "", batch_lot: "",
            current_stock: null, unit: null,
        });
    };
    const removeRow = (idx) => {
        if (rows[idx].kind !== "additional") return;
        setRows((p) => p.filter((_, i) => i !== idx));
    };
    const addExtraRow = () => {
        setRows((p) => [...p, {
            kind: "additional",
            bom_purpose: null, arch: null, row_label: "Unplanned consumable",
            material_id: null, chosen_material_id: "",
            category: "consumables",
            variant_id: "", variant_label: "",
            batch_id: "", batch_lot: "",
            expected_qty: 0, actual_qty: 0,
            substitution_reason: "",
            note: "",
            current_stock: null, unit: null,
        }]);
    };

    /* ── Derived ─────────────────────────────────────────────────────── */
    const materialsByCategory = useMemo(() => {
        const out = {};
        for (const m of allMaterials) {
            const c = m.category || "other";
            (out[c] = out[c] || []).push(m);
        }
        return out;
    }, [allMaterials]);

    /* Stock status: red if any BOM/additional row exceeds stock; amber if
       row is below reorder threshold; green otherwise. */
    const stockStatus = useMemo(() => {
        let outOfStock = null;
        let lowStock = null;
        for (const r of rows) {
            const mat = allMaterials.find((m) => m.id === r.chosen_material_id);
            if (!mat) continue;
            const stock = Number(mat.current_stock || 0);
            const need = Number(r.actual_qty || 0);
            const threshold = Number(mat.reorder_threshold || 0);
            if (need > stock) {
                outOfStock = outOfStock || { name: mat.name, sku: mat.sku, available: stock, need, unit: mat.unit };
            } else if (threshold > 0 && stock - need < threshold) {
                lowStock = lowStock || { name: mat.name, sku: mat.sku, remaining: Math.max(0, stock - need), unit: mat.unit };
            }
        }
        if (outOfStock) return { tone: "red", ...outOfStock };
        if (lowStock) return { tone: "amber", ...lowStock };
        return { tone: "green" };
    }, [rows, allMaterials]);

    /* Validation gates for the Confirm button */
    const validation = useMemo(() => {
        const errors = [];
        for (const r of rows) {
            if (!r.chosen_material_id) {
                errors.push(`Pick a material for "${r.row_label}"`);
                continue;
            }
            if (!r.batch_lot) {
                errors.push(`Batch / Lot # required for "${r.row_label}"`);
            }
            if (Number(r.actual_qty || 0) <= 0) {
                errors.push(`Quantity > 0 needed for "${r.row_label}"`);
            }
            const isSub = r.kind === "bom" && r.chosen_material_id !== r.material_id;
            if (isSub && !r.substitution_reason) {
                errors.push(`Substitution reason for "${r.row_label}"`);
            }
        }
        if (stockStatus.tone === "red") {
            errors.push(`Out of stock: ${stockStatus.sku}`);
        }
        return errors;
    }, [rows, stockStatus]);

    /* iter97.29 — Step 1 spec-completeness gate. Blocks "Next: Log
       materials" when the planner's spec is missing the minimum fields
       required to fabricate. */
    const step1Incomplete = useMemo(() => {
        if (!bom || !caseObj) return false;
        const spec = caseObj.manufacturing_spec || {};
        const plan = caseObj.plan || {};
        const sheetId = spec.aligner_sheet_material_id || spec.aligner_sheet_id;
        const sheetMat = sheetId ? (allMaterials || []).find((m) => m.id === sheetId) : null;
        const alignerBomLine = (bom?.lines || []).find((l) => (l.bom_purpose || "").startsWith("aligner_sheet"));
        const hasSheet = !!(sheetMat || alignerBomLine);
        const planU = Number(plan.aligner_count_upper || 0);
        const planL = Number(plan.aligner_count_lower || 0);
        const stages = Number(spec.aligner_stages_count || 0);
        const sps = Number(spec.sheets_per_stage || 0);
        const hasAlignerCount = (planU + planL > 0) || (stages > 0 && sps > 0);
        const arches = spec.arches || caseObj.arches;
        const hasArches = !!arches;
        return !hasSheet || !hasAlignerCount || !hasArches;
    }, [bom, caseObj, allMaterials]);

    const deductionPreview = useMemo(() => {
        return rows
            .filter((r) => r.chosen_material_id && Number(r.actual_qty || 0) > 0)
            .map((r) => {
                const m = allMaterials.find((x) => x.id === r.chosen_material_id);
                return `${r.actual_qty}${m?.unit ? "" : ""} ${m?.unit ? m.unit : ""} × ${m?.sku || "—"}`.trim();
            }).join(" · ");
    }, [rows, allMaterials]);

    /* ── Confirm flow ────────────────────────────────────────────────── */
    const confirm = async () => {
        if (validation.length > 0 || submitting) return;
        setSubmitting(true);
        try {
            // Step 1 — Acknowledge the case (idempotent — backend swallows
            // duplicate acks). Sets workflow to acknowledged + status to
            // in_production + starts the production timer.
            await api.post(`/production/cases/${caseId}/acknowledge`, {
                file_integrity_verified: true,
                notes: "Pre-production gate completed — spec reviewed + materials logged.",
            });
            // Step 2 — Materials log. Deducts inventory + writes
            // stock_movements + sets the case's logged_at stamp so QC gate
            // unlocks immediately.
            const phaseTag = phaseScope ? ` · Phase ${phaseScope.index + 1} of ${phaseScope.total} (stages ${phaseScope.stage_from}-${phaseScope.stage_to})` : "";
            const payload = {
                lines: rows
                    .filter((r) => r.chosen_material_id && Number(r.actual_qty || 0) > 0)
                    .map((r) => ({
                        material_id: r.material_id || r.chosen_material_id,
                        chosen_material_id: r.chosen_material_id,
                        expected_qty: Number(r.expected_qty || 0),
                        actual_qty: Number(r.actual_qty || 0),
                        bom_purpose: r.bom_purpose || null,
                        arch: r.arch || null,
                        variant_id: r.variant_id || null,
                        variant_label: r.variant_label || null,
                        batch_id: r.batch_id || null,
                        batch_lot: r.batch_lot || null,
                        is_additional: r.kind === "additional",
                        substitution_reason: r.kind === "bom" && r.chosen_material_id !== r.material_id
                            ? (r.substitution_reason || null) : null,
                        wastage_reason: null,  // No wastage at pickup time
                        note: phaseScope ? `phase=${phaseScope.index + 1}` : "",
                    })),
                technician_note: `Pre-production gate · materials pulled from stores${phaseTag}`,
            };
            await api.post(`/production/cases/${caseId}/materials-log`, payload);
            toast.success("Production started · Materials logged · Stock updated");
            onConfirmed?.();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Failed to begin production");
        } finally {
            setSubmitting(false);
        }
    };

    if (!caseId) return null;

    /* ── Render ──────────────────────────────────────────────────────── */
    return (
        <Dialog
            open={open}
            /* Per spec: do not close on backdrop click. The Dialog
               primitive bubbles a close on Esc/outside; we suppress that
               by ignoring "open=false" unless the caller pushed it. */
            modal={true}
            onOpenChange={() => { /* intentionally noop */ }}
        >
            <DialogContent
                className="max-w-3xl w-[92vw] p-0 max-h-[92vh] overflow-hidden flex flex-col"
                onPointerDownOutside={(e) => e.preventDefault()}
                onEscapeKeyDown={(e) => e.preventDefault()}
                data-testid="pre-production-gate-modal"
                data-step={step}
            >
                {/* Subheader — always visible */}
                <ModalSubheader caseObj={caseObj} step={step} phaseScope={phaseScope} />

                {/* Body */}
                <div className="flex-1 overflow-y-auto px-6 py-5">
                    {loading ? (
                        <div className="py-10 text-center text-xs text-muted-foreground">Loading case context…</div>
                    ) : step === 1 ? (
                        <Step1Spec
                            caseObj={caseObj}
                            bom={bom}
                            allMaterials={allMaterials}
                            plannerNote={plannerNote}
                            confirmed={confirmed1}
                            onToggleConfirm={(v) => setConfirmed1(v)}
                            specIncomplete={step1Incomplete}
                        />
                    ) : (
                        <Step2Materials
                            rows={rows}
                            phaseScope={phaseScope}
                            allMaterials={allMaterials}
                            materialsByCategory={materialsByCategory}
                            batchesByMat={batchesByMat}
                            stockStatus={stockStatus}
                            deductionPreview={deductionPreview}
                            validation={validation}
                            onUpdate={update}
                            onSetMaterial={setChosenMaterial}
                            onSetBatch={setBatch}
                            onSetBatchFreeText={setBatchFreeText}
                        />
                    )}
                </div>

                {/* Footer */}
                <ModalFooter
                    step={step}
                    canAdvance={step === 1 ? (confirmed1 && !step1Incomplete) : validation.length === 0}
                    submitting={submitting}
                    onCancel={onClose}
                    onBack={() => setStep(1)}
                    onNext={() => setStep(2)}
                    onConfirm={confirm}
                    validationCount={validation.length}
                    step1Incomplete={step1Incomplete}
                />
            </DialogContent>
        </Dialog>
    );
}


/* ─── Subheader ─────────────────────────────────────────────────────── */
function ModalSubheader({ caseObj, step, phaseScope }) {
    return (
        <div className="border-b border-border">
            <div className="px-6 py-3 bg-secondary/30 flex items-center justify-between gap-3 flex-wrap text-xs">
                <div className="flex items-center gap-3 flex-wrap text-muted-foreground">
                    <span className="font-mono font-bold text-foreground">{caseObj?.code || caseObj?.id?.slice(0, 8)}</span>
                    <span>·</span>
                    <span className="font-semibold text-foreground">{caseObj?.patient_name || "Patient"}</span>
                    <span>·</span>
                    <span>Dr. {(caseObj?.doctor_name || "").replace(/^Dr\.\s*/i, "")}</span>
                </div>
                <div className="flex items-center gap-2" data-testid="gate-step-indicator">
                    <span className="text-[10px] font-bold tracking-[0.16em] uppercase text-muted-foreground">
                        Step {step} of 2
                    </span>
                    <div className="flex items-center gap-1">
                        <span className={`w-6 h-1.5 rounded-full ${step >= 1 ? "bg-primary" : "bg-secondary"}`} />
                        <span className={`w-6 h-1.5 rounded-full ${step >= 2 ? "bg-primary" : "bg-secondary"}`} />
                    </div>
                </div>
            </div>
            <div className="px-6 py-3">
                <h2 className="font-display text-lg font-bold leading-tight">
                    {step === 1 ? "Pre-production gate · Manufacturing spec review" : "Pre-production gate · Material pickup log"}
                </h2>
                <p className="text-xs text-muted-foreground mt-0.5">
                    {step === 1
                        ? "Review the admin note and confirm you've read the spec before pulling materials."
                        : phaseScope
                            ? `Log the exact materials you are pulling for Phase ${phaseScope.index + 1} of ${phaseScope.total} (stages ${phaseScope.stage_from}–${phaseScope.stage_to}). Stock will be deducted from inventory once you confirm.`
                            : "Log the exact materials you are pulling from stores. Stock will be deducted from inventory once you confirm."}
                </p>
            </div>
        </div>
    );
}


/* ─── Footer ────────────────────────────────────────────────────────── */
function ModalFooter({ step, canAdvance, submitting, onCancel, onBack, onNext, onConfirm, validationCount, step1Incomplete }) {
    const nextTitle = step1Incomplete
        ? "Incomplete manufacturing spec — contact your planner."
        : !canAdvance
            ? "Tick the confirmation checkbox first"
            : "";
    return (
        <div className="border-t border-border px-6 py-3 bg-card flex items-center justify-between gap-3 flex-wrap">
            {step === 1 ? (
                <Button variant="outline" onClick={onCancel} disabled={submitting} data-testid="gate-cancel">
                    Cancel
                </Button>
            ) : (
                <Button variant="outline" onClick={onBack} disabled={submitting} data-testid="gate-back">
                    <ArrowLeft size={13} className="mr-1.5" /> Back to spec
                </Button>
            )}
            <div className="flex items-center gap-2">
                {step === 2 && validationCount > 0 && (
                    <span className="text-[10px] text-amber-700 font-semibold tabular-nums">{validationCount} field{validationCount === 1 ? "" : "s"} pending</span>
                )}
                {step === 1 ? (
                    <Button
                        onClick={onNext}
                        disabled={!canAdvance}
                        title={nextTitle}
                        data-testid="gate-next"
                    >
                        Next: Log materials <ArrowRight size={13} className="ml-1.5" weight="bold" />
                    </Button>
                ) : (
                    <Button
                        onClick={onConfirm}
                        disabled={!canAdvance || submitting}
                        title={!canAdvance ? "Enter batch/lot numbers for all materials to proceed." : ""}
                        className="bg-primary text-white"
                        data-testid="gate-confirm"
                    >
                        <PlayCircle size={13} className="mr-1.5" weight="fill" />
                        {submitting ? "Starting…" : "Confirm & begin production"}
                    </Button>
                )}
            </div>
        </div>
    );
}


/* ─── Step 1 ────────────────────────────────────────────────────────── */
function Step1Spec({ caseObj, bom, allMaterials, plannerNote, confirmed, onToggleConfirm, specIncomplete }) {
    const adminNote = caseObj?.production_assignment?.note;
    const adminNoteBy = caseObj?.production_assignment?.assigned_by_name;
    const adminNoteAt = caseObj?.production_assignment?.assigned_at;
    const spec = caseObj?.manufacturing_spec || {};
    const plan = caseObj?.plan || {};
    const lines = bom?.lines || [];

    /* Pull aggregate numbers from BOTH spec (planner authority) and plan
       (per-arch counts). The two should agree but historically may not — we
       show both so the tech can spot discrepancies.
       iter97.29: when the plan is empty but the spec has stages, fall back
       to the BOM's resolved per-arch counts (which already apply the spec
       fallback on the backend). */
    const bomU = (bom?.plan_aligners_upper) ?? 0;
    const bomL = (bom?.plan_aligners_lower) ?? 0;
    const alignerUpper = (plan.aligner_count_upper ?? 0) || bomU;
    const alignerLower = (plan.aligner_count_lower ?? 0) || bomL;
    const archesUsed = spec.arches === "both" ? "Upper + Lower"
        : spec.arches === "upper" ? "Upper only"
        : spec.arches === "lower" ? "Lower only"
        : (alignerUpper > 0 && alignerLower > 0 ? "Upper + Lower"
            : alignerUpper > 0 ? "Upper only"
            : alignerLower > 0 ? "Lower only" : "—");
    const stagesCount = spec.aligner_stages_count || 0;
    const sheetsPerStage = spec.sheets_per_stage || 1;
    const totalSheetsBOM = alignerUpper + alignerLower;
    const totalSheetsSpec = stagesCount * sheetsPerStage;

    /* Aligner sheet SKU + name — prefer the BOM-derived row (already
       resolved); fall back to scanning `allMaterials` if needed via the
       spec's aligner_sheet_material_id.
       iter97.29: ensures the spec's locked sheet type still renders even
       when the BOM endpoint returned zero aligner lines. */
    const alignerBomLine = lines.find((l) => (l.bom_purpose || "").startsWith("aligner_sheet"));
    const specSheetId = spec.aligner_sheet_material_id || spec.aligner_sheet_id;
    const specSheetMat = specSheetId
        ? (allMaterials || []).find((m) => m.id === specSheetId)
        : null;
    const alignerSheetLabel = alignerBomLine
        ? `${alignerBomLine.material_sku} · ${alignerBomLine.material_name}`
        : specSheetMat
            ? `${specSheetMat.sku} · ${specSheetMat.name}`
            : "—";

    const retainerLines = lines.filter((l) => (l.bom_purpose || "").startsWith("retainer"));
    const resinLine = lines.find((l) => l.bom_purpose === "resin");

    const archesPrinted = (() => {
        if (spec.arches === "upper") return "Upper only";
        if (spec.arches === "lower") return "Lower only";
        if (spec.arches === "both") return "Upper + Lower";
        const u = alignerUpper > 0;
        const l = alignerLower > 0;
        if (u && l) return "Upper + Lower";
        if (u) return "Upper only";
        if (l) return "Lower only";
        return "—";
    })();

    /* Phases (iter97.25) — surface the planner's phasing plan so the tech
       knows the dispatch sequence. Each phase covers a stage range; the
       sheet count is (stage_to - stage_from + 1) * sheets_per_stage. */
    const phases = spec.phases || [];

    /* Wear schedule overrides (planner can vary days per stage) */
    const wearDefault = plan.wear_schedule_default_days;
    const wearOverrides = plan.wear_schedule_overrides || [];

    /* Auxiliaries the planner asked for */
    const aux = [
        plan.aux_buttons && "Buttons",
        plan.aux_bite_ramps && "Bite ramps",
        plan.aux_mandibular_advancement && "Mandibular advancement",
        plan.aux_precision_cuts && "Precision cuts",
        plan.aux_hooks && "Hooks",
        (plan.precision_cuts || []).length > 0 && `Precision cuts (${plan.precision_cuts.length} site${plan.precision_cuts.length === 1 ? "" : "s"})`,
        (plan.attachments_plan || []).length > 0 && `Attachments (${plan.attachments_plan.length} tooth)`,
    ].filter(Boolean);

    /* iter97.29 — Aligners-on-plan display: shows the doctor's plan when
       available, the spec-derived per-arch counts when plan is empty. */
    const alignersOnPlanLabel = (alignerUpper > 0 || alignerLower > 0)
        ? `U${alignerUpper} · L${alignerLower}`
        : "—";

    /* iter97.29 — Total aligner sheets display: prefer the plan-derived
       BOM total when it agrees with the spec; otherwise show the spec's
       computed total (stages × sheets_per_stage). */
    const totalSheetsLabel = totalSheetsBOM
        ? `${totalSheetsBOM} (per BOM)${totalSheetsSpec && totalSheetsSpec !== totalSheetsBOM ? ` · ${totalSheetsSpec} (per spec ${stagesCount}×${sheetsPerStage})` : ""}`
        : totalSheetsSpec
            ? `${totalSheetsSpec} (per spec ${stagesCount}×${sheetsPerStage})`
            : "—";

    return (
        <div className="space-y-4">
            {/* iter97.29 — Spec-incomplete error banner. Visible when the
                planner's spec is missing the minimum fields. Blocks the
                Next button via parent-level state too. */}
            {specIncomplete && (
                <div
                    className="rounded-xl border-2 border-rose-300 bg-rose-50 px-4 py-3 flex items-start gap-3"
                    data-testid="gate-spec-incomplete-banner"
                >
                    <Warning size={18} weight="fill" className="text-rose-600 shrink-0 mt-0.5" />
                    <div className="flex-1 min-w-0">
                        <div className="font-semibold text-sm text-rose-900">
                            Incomplete manufacturing spec — contact your planner.
                        </div>
                        <div className="text-[12px] text-rose-800/90 mt-0.5">
                            Required fields missing: sheet type, arches, or aligner stages. Production cannot start until the planner completes the spec.
                        </div>
                    </div>
                </div>
            )}

            {adminNote && (
                <NoteBox
                    Icon={Bell}
                    tone="amber"
                    title="Note from admin"
                    body={adminNote}
                    author={adminNoteBy}
                    at={adminNoteAt}
                    testId="gate-admin-note"
                />
            )}
            {plannerNote?.notes && (
                <NoteBox
                    Icon={BookmarkSimple}
                    tone="teal"
                    title="Planner note"
                    body={plannerNote.notes}
                    author={plannerNote.planner_name}
                    at={plannerNote.updated_at}
                    testId="gate-planner-note"
                />
            )}

            {/* iter97.25 — Spec notes (free text on manufacturing_spec.notes).
                Distinct from the planner_notes blob — these are the planner's
                production-facing handover notes. */}
            {spec.notes && spec.notes.trim() && (
                <NoteBox
                    Icon={BookmarkSimple}
                    tone="teal"
                    title="Spec notes from planner"
                    body={spec.notes}
                    author={spec.set_by_name}
                    at={spec.set_at}
                    testId="gate-spec-notes"
                />
            )}

            <SpecCard
                title="Aligner spec"
                Icon={Stack}
                tone="violet"
                rows={[
                    ["Sheet type", alignerSheetLabel],
                    ["Arches", archesUsed],
                    ["Aligner stages (planner)", stagesCount ? `${stagesCount} stages` : "—"],
                    ["Sheets per stage", sheetsPerStage ? `${sheetsPerStage}` : "—"],
                    ["Aligners on plan", alignersOnPlanLabel],
                    ["Total aligner sheets needed", totalSheetsLabel],
                ]}
                testId="gate-spec-aligner"
            />

            {/* iter97.25 — Phases section. Only renders when the planner
                authored phases (default is single-shipment). Each phase
                surfaces stage range + sheet count so the tech can plan
                their dispatch. */}
            {phases.length > 0 && (
                <SpecCard
                    title="Dispatch phases (planner-authored)"
                    Icon={ArrowsLeftRight}
                    tone="emerald"
                    rows={phases.map((p, i) => {
                        const stageCount = (p.stage_to - p.stage_from + 1);
                        const phaseSheets = stageCount * sheetsPerStage;
                        return [
                            `Phase ${i + 1}`,
                            `Stages ${p.stage_from} → ${p.stage_to} · ${stageCount} stage${stageCount === 1 ? "" : "s"} · ~${phaseSheets} sheet${phaseSheets === 1 ? "" : "s"}`,
                        ];
                    })}
                    testId="gate-spec-phases"
                />
            )}

            {(retainerLines.length > 0 || (spec.retainer_count || 0) > 0) && (
                <SpecCard
                    title="Retainer spec"
                    Icon={Crown}
                    tone="sky"
                    rows={[
                        ["Sheet type", retainerLines[0]
                            ? `${retainerLines[0].material_sku} · ${retainerLines[0].material_name}`
                            : "—"],
                        ["Quantity", `${spec.retainer_count || retainerLines.reduce((s, l) => s + Number(l.expected_qty || 0), 0)} sheet${(spec.retainer_count || retainerLines.length) === 1 ? "" : "s"}`],
                        ["Arches", retainerLines.length > 0
                            ? retainerLines.map((l) => (l.arch || "?")).join(" + ")
                            : archesPrinted],
                        ["Retainer type (plan)", plan.retainer_type || "—"],
                    ]}
                    testId="gate-spec-retainer"
                />
            )}

            {resinLine && (
                <SpecCard
                    title="3D print spec"
                    Icon={Cube}
                    tone="amber"
                    rows={[
                        ["Resin type", `${resinLine.material_sku} · ${resinLine.material_name}`],
                        ["Models to print", archesPrinted],
                        ["Approx. resin (auto)", `${resinLine.expected_qty} ${resinLine.unit}`],
                    ]}
                    testId="gate-spec-resin"
                />
            )}

            {/* iter97.29 — Cleaning & packaging card removed per user
                request. IPA + packaging are deducted at later workflow
                stages, not at the pre-production gate, so they no longer
                appear in the spec review. */}

            {/* iter97.25 — Wear schedule + auxiliaries surfaced so the
                tech knows about any special instructions before fabrication. */}
            {(wearDefault || wearOverrides.length > 0 || aux.length > 0) && (
                <SpecCard
                    title="Plan auxiliaries"
                    Icon={GearSix}
                    tone="slate"
                    rows={[
                        wearDefault && ["Default wear interval", `${wearDefault} days per aligner`],
                        wearOverrides.length > 0 && [
                            "Wear overrides",
                            wearOverrides.map((o) => `Stage ${o.stage}: ${o.days}d`).join(" · "),
                        ],
                        aux.length > 0 && ["Auxiliaries", aux.join(" · ")],
                    ].filter(Boolean)}
                    testId="gate-spec-aux"
                />
            )}

            {/* Confirmation gate */}
            <label
                className={`mt-4 flex items-start gap-2.5 rounded-xl border-2 px-4 py-3 cursor-pointer transition-colors ${
                    confirmed ? "border-emerald-300 bg-emerald-50/60" : "border-border bg-secondary/30 hover:border-primary/30"
                }`}
                data-testid="gate-confirm-checkbox-row"
            >
                <Checkbox
                    checked={confirmed}
                    onCheckedChange={(v) => onToggleConfirm(!!v)}
                    className="mt-0.5"
                    data-testid="gate-confirm-checkbox"
                />
                <span className="text-sm text-foreground leading-snug">
                    I have read the admin note, reviewed the manufacturing spec, and will pull materials accordingly from stores.
                </span>
            </label>
        </div>
    );
}


function NoteBox({ Icon, tone, title, body, author, at, testId }) {
    const toneCls = tone === "amber"
        ? { ring: "border-amber-200 bg-amber-50", icon: "text-amber-700", label: "text-amber-900" }
        : tone === "teal"
            ? { ring: "border-teal-200 bg-teal-50", icon: "text-teal-700", label: "text-teal-900" }
            : { ring: "border-border bg-card", icon: "text-muted-foreground", label: "text-foreground" };
    const dt = at ? new Date(at) : null;
    const dtLabel = dt
        ? dt.toLocaleString("en-IN", { day: "numeric", month: "short", year: "numeric", hour: "numeric", minute: "2-digit" })
        : "";
    return (
        <div className={`rounded-xl border ${toneCls.ring} p-3`} data-testid={testId}>
            <div className={`text-[10px] font-bold tracking-[0.18em] uppercase ${toneCls.label} flex items-center gap-1.5 mb-1`}>
                <Icon size={11} weight="fill" className={toneCls.icon} /> {title}
            </div>
            <div className="text-[13px] leading-snug text-foreground/90 whitespace-pre-wrap">{body}</div>
            {(author || dtLabel) && (
                <div className="text-[11px] text-muted-foreground mt-2 text-right">
                    — {author || "Unknown"}{dtLabel ? ` · ${dtLabel}` : ""}
                </div>
            )}
        </div>
    );
}


function SpecCard({ title, Icon, tone, rows, testId }) {
    const t = TONE_BG[tone] || TONE_BG.slate;
    return (
        <div className="rounded-xl border border-border bg-card overflow-hidden" data-testid={testId}>
            <div className="px-4 py-2 bg-secondary/30 border-b border-border flex items-center gap-2">
                <div className={`w-6 h-6 rounded-md ${t} flex items-center justify-center`}>
                    <Icon size={13} weight="duotone" />
                </div>
                <h3 className="font-display font-semibold text-sm">{title}</h3>
            </div>
            <div className="p-3 grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1.5">
                {rows.map(([k, v]) => (
                    <div key={k} className="flex items-start justify-between gap-2 text-xs border-b border-border/40 pb-1.5 last:border-b-0">
                        <span className="text-muted-foreground">{k}</span>
                        <span className="font-semibold text-foreground text-right truncate" title={String(v)}>{v}</span>
                    </div>
                ))}
            </div>
        </div>
    );
}


/* ─── Step 2 ────────────────────────────────────────────────────────── */
function Step2Materials({
    rows, phaseScope, allMaterials, materialsByCategory, batchesByMat,
    stockStatus, deductionPreview, validation,
    onUpdate, onSetMaterial, onSetBatch, onSetBatchFreeText,
}) {
    return (
        <div className="space-y-3">
            {/* iter97.26 — Phase banner. Only renders when the planner
                authored phases. Tells the tech exactly what slice of the
                case they're pulling materials for right now. */}
            {phaseScope && <PhaseBanner scope={phaseScope} />}

            {/* iter97.27 — Helper banner explaining the locked-by-default
                pattern. Sets the tech's expectation that this is a
                batch-logging step, not a material-choosing step. */}
            <div className="rounded-lg bg-secondary/40 border border-border px-3 py-2 text-[11px] text-muted-foreground" data-testid="gate-step2-helper">
                <span className="font-semibold text-foreground">Heads up:</span> Material types + quantities are locked from the planner&apos;s spec. Your only job here is to enter the <span className="font-semibold text-foreground">batch / lot number</span> for each row. If stock for a specified SKU has run out, use the <span className="font-semibold text-amber-700">Substitute?</span> link on the row to swap.
            </div>

            {rows.map((r, idx) => (
                <PickupRowCard
                    key={`${r.kind}-${r.bom_purpose || "x"}-${idx}`}
                    row={r}
                    idx={idx}
                    allMaterials={allMaterials}
                    materialsByCategory={materialsByCategory}
                    batches={batchesByMat[r.chosen_material_id] || []}
                    onUpdate={(patch) => onUpdate(idx, patch)}
                    onSetMaterial={(mid) => onSetMaterial(idx, mid)}
                    onSetBatch={(bid) => onSetBatch(idx, bid)}
                    onSetBatchFreeText={(lot) => onSetBatchFreeText(idx, lot)}
                />
            ))}

            <StockStatusBar status={stockStatus} />

            {deductionPreview && (
                <div className="text-[11px] text-muted-foreground px-1" data-testid="gate-deduction-preview">
                    On confirm, the following will be deducted from inventory: <span className="font-mono">{deductionPreview}</span>
                </div>
            )}

            {validation.length > 0 && (
                <div className="text-[11px] text-rose-700 bg-rose-50/60 border border-rose-200 rounded-lg px-3 py-2" data-testid="gate-validation">
                    <div className="font-semibold mb-0.5">Fix before confirming:</div>
                    <ul className="list-disc list-inside leading-tight">
                        {validation.slice(0, 4).map((m, i) => <li key={i}>{m}</li>)}
                        {validation.length > 4 && <li>…{validation.length - 4} more</li>}
                    </ul>
                </div>
            )}
        </div>
    );
}


function StockStatusBar({ status }) {
    if (status.tone === "red") {
        return (
            <div className="rounded-xl border-2 border-rose-300 bg-rose-50 px-4 py-3 text-sm font-semibold text-rose-900 flex items-center gap-2" data-testid="gate-stock-status" data-tone="red">
                <Warning size={14} weight="fill" className="text-rose-700 shrink-0" />
                <span>{status.sku} is out of stock — only {status.available} {status.unit} available, you need {status.need} — cannot proceed.</span>
            </div>
        );
    }
    if (status.tone === "amber") {
        return (
            <div className="rounded-xl border-2 border-amber-300 bg-amber-50 px-4 py-3 text-sm font-semibold text-amber-900 flex items-center gap-2" data-testid="gate-stock-status" data-tone="amber">
                <Warning size={14} weight="fill" className="text-amber-700 shrink-0" />
                <span>{status.sku} is low — only {status.remaining} {status.unit} will remain after this pickup.</span>
            </div>
        );
    }
    return (
        <div className="rounded-xl border-2 border-emerald-300 bg-emerald-50 px-4 py-3 text-sm font-semibold text-emerald-900 flex items-center gap-2" data-testid="gate-stock-status" data-tone="green">
            <CheckCircle size={14} weight="fill" className="text-emerald-700 shrink-0" /> All materials available in stores.
        </div>
    );
}


function PickupRowCard({
    row, idx, allMaterials, materialsByCategory, batches,
    onUpdate, onSetMaterial, onSetBatch, onSetBatchFreeText,
}) {
    const cat = CAT[row.category] || CAT.other;
    const CatIcon = cat.Icon;
    const isSub = row.chosen_material_id && row.chosen_material_id !== row.material_id;
    /* iter97.27 — Locked by default. Tech must explicitly click
       "Substitute?" to unlock the SKU dropdown. Once subbed (or
       still in-flight unlock), we keep it open. */
    const [substitutionUnlocked, setSubstitutionUnlocked] = useState(false);
    const skuEditable = isSub || substitutionUnlocked;

    const chosenMat = allMaterials.find((m) => m.id === row.chosen_material_id);
    const skuOptions = materialsByCategory?.[row.category] || allMaterials.filter((m) => m.category === row.category);

    const cardTone = isSub
        ? "border-amber-300 bg-amber-50/40"
        : "border-border bg-card";

    const cancelSubstitute = () => {
        setSubstitutionUnlocked(false);
        // Revert chosen back to BOM default + clear reason
        onUpdate({
            chosen_material_id: row.material_id,
            substitution_reason: "",
        });
    };

    return (
        <div
            className={`rounded-xl border-2 ${cardTone} p-3 sm:p-4 space-y-3`}
            data-testid={`gate-row-${idx}`}
            data-row-kind="bom"
            data-substitution={isSub ? "1" : "0"}
        >
            {/* Header — category icon + row label + BOM/SUBSTITUTED badge */}
            <div className="flex items-start justify-between gap-2">
                <div className="flex items-center gap-2 min-w-0">
                    <div className={`w-8 h-8 rounded-lg ${TONE_BG[cat.tone] || TONE_BG.slate} flex items-center justify-center shrink-0`}>
                        <CatIcon size={15} weight="duotone" />
                    </div>
                    <div className="min-w-0">
                        <div className="font-display font-semibold text-sm truncate" data-testid={`gate-row-label-${idx}`}>
                            {row.row_label || cat.label}
                        </div>
                        <div className="text-[10px] text-muted-foreground">Locked by planner spec</div>
                    </div>
                </div>
                <div className="flex items-center gap-1.5 shrink-0">
                    {isSub ? (
                        <span className="inline-flex items-center gap-1 text-[9px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 border border-amber-200" data-testid={`gate-row-badge-substituted-${idx}`}>
                            <ArrowsLeftRight size={9} weight="bold" /> Substituted
                        </span>
                    ) : (
                        <span className="inline-flex items-center text-[9px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded bg-slate-100 text-slate-700 border border-slate-200" data-testid={`gate-row-badge-bom-${idx}`}>
                            BOM
                        </span>
                    )}
                </div>
            </div>

            {/* Substitution warning + reason (only when subbed) */}
            {isSub && (
                <div className="rounded-lg bg-amber-100/60 border border-amber-300 px-3 py-2 text-[11px] text-amber-900 space-y-2" data-testid={`gate-substitution-banner-${idx}`}>
                    <div className="flex items-start gap-1.5">
                        <Warning size={11} weight="fill" className="text-amber-700 mt-0.5 shrink-0" />
                        <div>
                            Substitution detected — spec says <span className="font-mono font-bold">{row.material_default_sku}</span>. Please provide a reason.
                        </div>
                    </div>
                    <Select value={row.substitution_reason || ""} onValueChange={(v) => onUpdate({ substitution_reason: v })}>
                        <SelectTrigger className="h-8 text-xs w-full bg-white" data-testid={`gate-substitution-reason-${idx}`}>
                            <SelectValue placeholder="Why did you substitute?" />
                        </SelectTrigger>
                        <SelectContent>
                            {SUBSTITUTION_REASONS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                        </SelectContent>
                    </Select>
                </div>
            )}

            {/* Material row — locked text by default + Substitute? link */}
            <div>
                <FieldLabel>Material</FieldLabel>
                {skuEditable ? (
                    <div className="flex items-start gap-2">
                        <Select value={row.chosen_material_id || ""} onValueChange={onSetMaterial} disabled={skuOptions.length === 0}>
                            <SelectTrigger
                                className={`h-9 text-xs flex-1 ${isSub ? "border-amber-400" : "border-primary/40"}`}
                                data-testid={`gate-sku-${idx}`}
                            >
                                <SelectValue placeholder={skuOptions.length === 0 ? "No SKUs in category" : "Pick a substitute SKU"} />
                            </SelectTrigger>
                            <SelectContent>
                                {skuOptions.map((m) => (
                                    <SelectItem key={m.id} value={m.id}>
                                        <span className="font-mono">{m.sku}</span>
                                        <span className="text-muted-foreground ml-2">· {m.name}</span>
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                        <button
                            type="button"
                            onClick={cancelSubstitute}
                            className="text-[11px] font-semibold text-muted-foreground hover:text-foreground self-center px-2"
                            data-testid={`gate-substitute-cancel-${idx}`}
                        >
                            Cancel
                        </button>
                    </div>
                ) : (
                    <div className="flex items-center justify-between gap-2 h-9 px-3 rounded-md bg-muted/40 border border-border" data-testid={`gate-sku-locked-${idx}`}>
                        <div className="flex items-center gap-2 min-w-0">
                            <Lock size={11} weight="bold" className="text-muted-foreground shrink-0" />
                            <span className="font-mono text-xs font-semibold truncate">
                                {chosenMat?.sku || row.material_default_sku}
                            </span>
                            <span className="text-muted-foreground text-xs truncate">
                                · {chosenMat?.name || row.material_default_name}
                            </span>
                        </div>
                        <button
                            type="button"
                            onClick={() => setSubstitutionUnlocked(true)}
                            className="text-[11px] font-semibold text-amber-700 hover:text-amber-800 hover:underline shrink-0"
                            data-testid={`gate-substitute-link-${idx}`}
                            title="Open the SKU dropdown to log a stock substitution"
                        >
                            Substitute?
                        </button>
                    </div>
                )}
            </div>

            {/* Quantity (read-only) + Batch/Lot (editable, required) */}
            <div className="grid grid-cols-1 sm:grid-cols-[1fr_2fr] gap-2">
                <div>
                    <FieldLabel>Pulling from stores</FieldLabel>
                    <div
                        className="h-9 px-3 rounded-md bg-muted/40 border border-border text-xs flex items-center font-semibold tabular-nums gap-1.5"
                        data-testid={`gate-qty-${idx}`}
                    >
                        <Lock size={10} weight="bold" className="text-muted-foreground" />
                        {Number(row.expected_qty).toLocaleString("en-IN")} {chosenMat?.unit || row.unit}
                    </div>
                    <div className="text-[10px] text-muted-foreground mt-1 truncate">
                        {row.qty_source === "bom" || !row.qty_source
                            ? <>Calculated from spec</>
                            : (
                                Number(row.bom_expected_qty) !== Number(row.expected_qty)
                                    ? <>Phase qty · case BOM total: {Number(row.bom_expected_qty).toLocaleString("en-IN")} {row.unit}</>
                                    : <>Calculated from spec</>
                            )
                        }
                    </div>
                </div>
                <div>
                    <FieldLabel>
                        Batch / Lot <span className="text-rose-600 ml-0.5">*</span>
                    </FieldLabel>
                    {batches.length > 0 ? (
                        <Select value={row.batch_id || ""} onValueChange={onSetBatch}>
                            <SelectTrigger className={`h-9 text-xs ${!row.batch_lot ? "border-rose-300" : ""}`} data-testid={`gate-batch-${idx}`}>
                                <SelectValue placeholder="Pick batch" />
                            </SelectTrigger>
                            <SelectContent>
                                {batches.map((b) => (
                                    <SelectItem key={b.id} value={b.id}>
                                        <span className="font-mono">{b.lot_number}</span>
                                        <span className="text-muted-foreground ml-1">· {Number(b.qty_remaining || 0).toFixed(1)} left</span>
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    ) : (
                        <Input
                            value={row.batch_lot || ""}
                            onChange={(e) => onSetBatchFreeText(e.target.value)}
                            placeholder="LOT-2026-05-A"
                            className={`h-9 text-xs font-mono ${!row.batch_lot ? "border-rose-300" : ""}`}
                            data-testid={`gate-batch-freetext-${idx}`}
                        />
                    )}
                </div>
            </div>
        </div>
    );
}


function FieldLabel({ children }) {
    return (
        <label className="text-[10px] font-bold tracking-[0.16em] uppercase text-muted-foreground block mb-0.5">{children}</label>
    );
}


/* iter97.26 — Phase banner for Step 2. Renders only when planner phases
   are present. Communicates the phased fabrication contract: "you're
   pulling materials for Phase N of M (stages X→Y). Phase M+1 materials
   will be pulled in a separate gate after this phase ships." */
function PhaseBanner({ scope }) {
    const phaseLabel = `Phase ${scope.index + 1} of ${scope.total}`;
    const isLast = scope.is_last;
    return (
        <div
            className="rounded-xl border-2 border-emerald-300 bg-emerald-50/70 px-4 py-3 flex items-start gap-3"
            data-testid="gate-phase-banner"
            data-phase-index={scope.index}
        >
            <div className="w-9 h-9 rounded-lg bg-emerald-100 text-emerald-700 flex items-center justify-center shrink-0">
                <ArrowsLeftRight size={16} weight="duotone" />
            </div>
            <div className="flex-1 min-w-0">
                <div className="text-[10px] font-bold tracking-[0.18em] uppercase text-emerald-900 mb-0.5">
                    {phaseLabel}
                </div>
                <div className="font-display font-semibold text-sm text-emerald-900">
                    Pulling materials for stages {scope.stage_from} → {scope.stage_to}
                    <span className="text-emerald-700 font-normal ml-1">· {scope.stages_in_phase} stage{scope.stages_in_phase === 1 ? "" : "s"}</span>
                </div>
                <div className="text-[11px] text-emerald-800/80 mt-1 leading-snug">
                    {isLast
                        ? `Final phase — retainers are included in this pickup.`
                        : `Retainers and remaining aligner stages will be pulled in a separate gate after Phase ${scope.index + 1} ships.`}
                </div>
            </div>
        </div>
    );
}
